import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ReceiptText, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORIES, CATEGORY_COLORS, fmtMoney } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function CategoryPill({ category }) {
  const color = CATEGORY_COLORS[category] || "#94A3B8";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: `${color}22`, color }}
    >
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {category}
    </span>
  );
}

export default function Receipts() {
  const navigate = useNavigate();
  const [all, setAll] = useState(null);
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    try {
      const list = await api("/receipts?limit=500");
      setAll(list);
    } catch {
      setAll([]);
    }
  }, []);

  useEffect(() => {
    document.title = "Receipts · RigBooks";
    load();
  }, [load]);

  const shown = useMemo(() => {
    if (!all) return [];
    return filter === "All" ? all : all.filter((r) => r.category === filter);
  }, [all, filter]);

  const chips = ["All", ...CATEGORIES];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-rig-dim">
          {all ? `${all.length} receipt${all.length === 1 ? "" : "s"} in your audit archive` : "Loading…"}
        </p>
        <Button
          data-testid="add-receipt-btn"
          onClick={() => navigate("/add")}
          className="bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
        >
          <Plus className="mr-1 h-4 w-4" strokeWidth={3} /> New Receipt
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c.toLowerCase()}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
              filter === c
                ? "border-rig-accent bg-rig-accent text-rig-accentText"
                : "border-rig-border bg-rig-surface text-rig-dim hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {!all ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 bg-rig-surface" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-rig-borderStrong bg-rig-surface p-14 text-center">
          <ReceiptText className="h-12 w-12 text-rig-mute" />
          <p className="mt-4 font-display text-xl uppercase tracking-wide text-white">No receipts here</p>
          <p className="mt-1 text-sm text-rig-dim">
            {filter === "All" ? "Add your first receipt to get started." : `No ${filter} receipts logged.`}
          </p>
          <Button
            onClick={() => navigate("/add")}
            className="mt-5 bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
          >
            <Plus className="mr-1 h-4 w-4" strokeWidth={3} /> Add Receipt
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-rig-border">
          <table className="w-full text-left">
            <thead className="bg-rig-surface2">
              <tr className="font-display text-[11px] uppercase tracking-widest text-rig-dim">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Vendor</th>
                <th className="hidden px-4 py-3 sm:table-cell">Category</th>
                <th className="hidden px-4 py-3 md:table-cell">Location</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/receipts/${r.id}`)}
                  data-testid={`receipt-item-${r.id}`}
                  className="cursor-pointer border-t border-rig-border bg-rig-surface transition-colors hover:bg-rig-surface2"
                >
                  <td className="whitespace-nowrap px-4 py-3.5 text-sm text-rig-dim">{r.receipt_date}</td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2 font-semibold text-white">
                      {r.image_base64 ? (
                        <ImageIcon className="h-4 w-4 shrink-0 text-rig-accent" />
                      ) : null}
                      <span className="truncate">{r.vendor}</span>
                    </div>
                    <div className="mt-0.5 sm:hidden">
                      <CategoryPill category={r.category} />
                    </div>
                  </td>
                  <td className="hidden px-4 py-3.5 sm:table-cell">
                    <CategoryPill category={r.category} />
                  </td>
                  <td className="hidden max-w-[180px] truncate px-4 py-3.5 text-sm text-rig-dim md:table-cell">
                    {r.gps_label || "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-right font-display text-lg font-bold text-white">
                    {fmtMoney(r.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
